import { EngineInput, NoteRecord, EngineResult } from "./types";
import { process } from "./production/engine";
import {
  saveNote,
  getNotes as loadNotes,
  updateNote,
  getQueue as loadQueue,
  dequeue,
  enqueue,
  getDictionaryMappings,
  getUnsyncedTrainingLog,
  saveTrainingLog,
  getUnsyncedRawAudio,
  markRawAudioSynced,
  deleteRawAudio
} from "./offline_storage";
import { pwaApi } from "../api";

function generateId(): string {
  return `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// Side-channel do powiązania nagrania audio (zapisanego równolegle do
// SpeechRecognition, które samo nie daje dostępu do audio) z tworzoną notatką —
// bez przeciągania parametru przez cały łańcuch wywołań UI -> useNavigation -> tutaj.
let pendingAudioHash: string | undefined;
export function setPendingAudioHash(hash: string | undefined): void {
  pendingAudioHash = hash;
}

export async function recordNote(input: EngineInput, audioHash?: string): Promise<NoteRecord & { result: EngineResult }> {
  const resolvedAudioHash = audioHash ?? pendingAudioHash;
  pendingAudioHash = undefined;
  const result = await process(input);
  const record: NoteRecord = {
    id: generateId(),
    createdAt: Date.now(),
    transcript: input.transcript,
    script: result.script,
    shortNote: result.shortNote,
    title: result.title,
    entities: result.entities,
    path: result.path,
    industry: input.industry,
    plantId: input.plantId,
    managerId: input.managerId,
    synced: false,
    completed: false,
    guardStatus: result.guardStatus,
    warningText: result.warningText,
    reason: result.reason,
    trace: result.trace,
    audioHash: resolvedAudioHash,
  };
  await saveNote(record);
  await enqueue(record.id);
  return { ...record, result };
}

export function getNotes(): Promise<NoteRecord[]> {
  return loadNotes();
}

export async function markCompleted(id: string): Promise<void> {
  const note = await loadNotes().then(notes => notes.find(n => n.id === id));
  if (note) {
    note.completed = !note.completed;
    await updateNote(note);
  }
}

export function getQueue(): Promise<string[]> {
  return loadQueue();
}

// Widoczny w UI stan "trwa synchronizacja" — bez tego użytkownik nie miał
// żadnego dowodu, że sync faktycznie się odpalił, tylko licznik "do wysłania",
// który zmienia się skokowo dopiero po zakończeniu.
let syncInProgress = false;
export function isSyncInProgress(): boolean {
  return syncInProgress;
}

export async function sync(dryRun = false): Promise<NoteRecord[]> {
  syncInProgress = true;
  try {
    return await syncInternal(dryRun);
  } finally {
    syncInProgress = false;
  }
}

async function syncInternal(dryRun: boolean): Promise<NoteRecord[]> {
  const queue = await loadQueue();
  const notes = await loadNotes();
  const synced: NoteRecord[] = [];

  for (const id of queue) {
    const note = notes.find(n => n.id === id);
    if (!note) continue;

    if (!dryRun) {
      try {
        await pwaApi.syncOfflineNote(note);
      } catch (e: any) {
        console.warn('[offline_pipeline] sync failed for', note.id, e.message);
        break;
      }
    }

    note.synced = true;
    await updateNote(note);
    await dequeue(id);
    synced.push(note);
  }

  return synced;
}

// Samouczenie: wysyła zebrane mapowania słownikowe (statystyki dopasowań) oraz
// niezsynchronizowany log treningowy (nierozpoznane słowa, poprawki użytkownika)
// do backendu — do dalszej (ręcznej) kuracji słownika zakładowego.
// Wywoływane cyklicznie/na 'online' przez useNetworkSync — nie blokuje UI.
export async function syncLearningData(): Promise<{ mappings: number; trainingLog: number }> {
  const mappings = await getDictionaryMappings();
  const unsyncedLog = await getUnsyncedTrainingLog();

  if (mappings.length === 0 && unsyncedLog.length === 0) {
    return { mappings: 0, trainingLog: 0 };
  }

  await pwaApi.syncOfflineLearning(mappings, unsyncedLog);

  for (const entry of unsyncedLog) {
    entry.synced = true;
    await saveTrainingLog(entry);
  }

  return { mappings: mappings.length, trainingLog: unsyncedLog.length };
}

// Wysyła zaległe nagrania audio (offline i online — obie ścieżki zapisują je do
// tego samego store'u `raw_audio`) na serwer i usuwa lokalną kopię po udanym
// uploadzie — telefon nie ma gromadzić głosówek bez końca. Nagranie zostaje
// lokalnie, dopóki upload się nie powiedzie (bezpieczna kolejka).
export async function syncAudio(): Promise<{ uploaded: number; failed: number }> {
  const pending = await getUnsyncedRawAudio();

  let uploaded = 0;
  let failed = 0;

  for (const { hash, blob, noteId } of pending) {
    try {
      await pwaApi.uploadRawAudio(hash, blob, noteId);
      await markRawAudioSynced(hash);
      await deleteRawAudio(hash);
      uploaded++;
    } catch (e: any) {
      console.warn('[offline_pipeline] audio upload failed for', hash, e.message);
      failed++;
      break; // zachowaj kolejność, nie zalewaj serwera przy słabym łączu
    }
  }

  return { uploaded, failed };
}
